import React, { useEffect, useRef, useState } from "react";
import { Upload, Sparkles, Trash2, Loader2, Palette as PaletteIcon, Save as SaveIcon } from "lucide-react";
import DashboardLayout from "@/components/app/DashboardLayout";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { FONT_FAMILIES } from "@/components/app/canvasModel";

const headingFont = { fontFamily: "Outfit, sans-serif" };
const accentFont  = { fontFamily: "'Playfair Display', serif", fontStyle: "italic", fontWeight: 500 };

const DEFAULT_KIT = {
  logo_file_id: null,
  logo_url: null,
  primary:   "#0EA5E9",
  secondary: "#0F172A",
  accent:    "#F59E0B",
  dark:      "#0F172A",
  light:     "#F8FAFC",
  font_heading: "Outfit",
  font_body:    "Outfit",
};

export default function BrandKit() {
  const [kit, setKit] = useState(DEFAULT_KIT);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [extracting, setExtracting] = useState(false);
  const [uploading, setUploading]   = useState(false);
  const fileRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get("/brand-kit");
        if (r.data.brand_kit) setKit({ ...DEFAULT_KIT, ...r.data.brand_kit });
      } catch (err) {
        toast.error(formatApiError(err));
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const set = (k, v) => setKit((prev) => ({ ...prev, [k]: v }));

  const onLogoSelected = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      // 1. Upload the file
      const fd = new FormData();
      fd.append("file", file);
      const up = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const logoUrl = up.data.url;

      // 2. Auto-extract palette
      setExtracting(true);
      const fd2 = new FormData();
      fd2.append("file", file);
      const pl = await api.post("/brand-kit/extract-palette", fd2, { headers: { "Content-Type": "multipart/form-data" } });
      const cols = pl.data.palette || [];

      const next = {
        ...kit,
        logo_file_id: up.data.id,
        logo_url: logoUrl,
        primary:   cols[0] || kit.primary,
        secondary: cols[1] || kit.secondary,
        accent:    cols[2] || kit.accent,
        dark:      cols[3] || kit.dark,
        light:     cols[4] || kit.light,
      };
      setKit(next);
      toast.success("Logo uploaded & palette extracted");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setUploading(false);
      setExtracting(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const removeLogo = () => set("logo_file_id", null) || set("logo_url", null);

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/brand-kit", {
        logo_file_id: kit.logo_file_id,
        primary:   kit.primary,
        secondary: kit.secondary,
        accent:    kit.accent,
        dark:      kit.dark,
        light:     kit.light,
        font_heading: kit.font_heading,
        font_body:    kit.font_body,
      });
      toast.success("Brand Kit saved");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="p-10 flex items-center justify-center text-slate-400">
          <Loader2 className="h-6 w-6 animate-spin" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="p-6 md:p-10 max-w-5xl text-slate-900">
        <div className="mb-8">
          <div className="text-xs uppercase tracking-[0.3em] text-sky-600 mb-3">Brand Kit</div>
          <h1 className="text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.05] font-semibold" style={headingFont}>
            Your brand, <span style={accentFont} className="text-sky-600">applied everywhere</span>
          </h1>
          <p className="mt-3 text-sm text-slate-500 max-w-2xl">
            Upload your logo once. We&apos;ll pull your colours automatically. Hit the &quot;Brand&quot; button in any
            design and we&apos;ll instantly recolour every text and shape to match.
          </p>
        </div>

        {/* Logo card */}
        <Card title="Logo" testid="brand-logo-card">
          <input
            ref={fileRef}
            type="file"
            accept="image/*"
            hidden
            onChange={(e) => onLogoSelected(e.target.files?.[0])}
            data-testid="brand-logo-input"
          />
          <div className="flex flex-col sm:flex-row items-center gap-6">
            <div
              className="w-40 h-40 rounded-2xl border-2 border-dashed border-slate-200 bg-slate-50 flex items-center justify-center overflow-hidden"
              style={{ backgroundColor: kit.light }}
            >
              {kit.logo_url ? (
                <img src={kit.logo_url} alt="Logo" className="max-w-full max-h-full object-contain" data-testid="brand-logo-preview" />
              ) : (
                <PaletteIcon className="h-10 w-10 text-slate-300" />
              )}
            </div>
            <div className="flex-1">
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={() => fileRef.current?.click()}
                  disabled={uploading || extracting}
                  className="rounded-full bg-sky-500 hover:bg-sky-600 text-white"
                  data-testid="brand-logo-upload-btn"
                >
                  {uploading ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> : <Upload className="h-4 w-4 mr-1.5" />}
                  {uploading ? "Uploading…" : kit.logo_url ? "Replace logo" : "Upload logo"}
                </Button>
                {kit.logo_url && (
                  <Button onClick={removeLogo} variant="outline" className="rounded-full" data-testid="brand-logo-remove">
                    <Trash2 className="h-4 w-4 mr-1.5 text-rose-500" /> Remove
                  </Button>
                )}
              </div>
              <div className="text-xs text-slate-500 mt-3">
                We&apos;ll auto-extract 5 brand colours from the logo. You can fine-tune them below.
              </div>
            </div>
          </div>
        </Card>

        {/* Colors card */}
        <Card title="Brand colours" testid="brand-colors-card" extra={
          extracting ? <span className="text-xs text-sky-600 flex items-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Extracting…</span> : null
        }>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              ["primary",   "Primary"],
              ["secondary", "Secondary"],
              ["accent",    "Accent"],
              ["dark",      "Dark"],
              ["light",     "Light"],
            ].map(([key, label]) => (
              <ColorField key={key} label={label} value={kit[key]} onChange={(v) => set(key, v)} testid={`brand-color-${key}`} />
            ))}
          </div>
        </Card>

        {/* Fonts card */}
        <Card title="Fonts" testid="brand-fonts-card">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <FontField label="Heading font" value={kit.font_heading} onChange={(v) => set("font_heading", v)} testid="brand-font-heading" />
            <FontField label="Body font"    value={kit.font_body}    onChange={(v) => set("font_body", v)}    testid="brand-font-body" />
          </div>
        </Card>

        {/* Preview card */}
        <Card title="Preview" testid="brand-preview-card">
          <div
            className="rounded-2xl p-8 overflow-hidden"
            style={{ background: kit.light, color: kit.dark, border: `1px solid ${kit.dark}20` }}
          >
            <div className="flex items-center gap-4 mb-6">
              {kit.logo_url && <img src={kit.logo_url} alt="Logo" className="h-10 w-10 object-contain" />}
              <div className="text-2xl font-bold" style={{ fontFamily: fontCss(kit.font_heading), color: kit.dark }}>
                Your Brand Name
              </div>
            </div>
            <div className="text-5xl font-extrabold mb-3" style={{ fontFamily: fontCss(kit.font_heading), color: kit.primary }}>
              Big bold headline
            </div>
            <div className="text-lg mb-6" style={{ fontFamily: fontCss(kit.font_body), color: kit.secondary }}>
              This is how your designs will feel — clean, on-brand, ready to ship.
            </div>
            <div className="flex flex-wrap gap-3">
              <div className="px-5 py-2.5 rounded-full text-white text-sm font-semibold" style={{ background: kit.primary }}>Primary CTA</div>
              <div className="px-5 py-2.5 rounded-full text-white text-sm font-semibold" style={{ background: kit.accent }}>Accent badge</div>
              <div className="px-5 py-2.5 rounded-full text-sm font-semibold border" style={{ borderColor: kit.dark, color: kit.dark }}>Outline pill</div>
            </div>
          </div>
        </Card>

        {/* Save bar */}
        <div className="sticky bottom-4 mt-4 flex justify-end">
          <Button
            onClick={save}
            disabled={saving}
            className="rounded-full h-11 px-6 bg-sky-500 hover:bg-sky-600 text-white shadow-lg"
            data-testid="brand-save-btn"
          >
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <SaveIcon className="h-4 w-4 mr-2" />}
            Save Brand Kit
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}

function fontCss(id) {
  const f = FONT_FAMILIES.find((f) => f.id === id);
  return f ? f.css : id || "Outfit, sans-serif";
}

function Card({ title, children, extra = null, testid }) {
  return (
    <div className="bg-white border border-slate-200 rounded-3xl p-6 mb-5" data-testid={testid}>
      <div className="flex items-center justify-between mb-4">
        <div className="text-xs uppercase tracking-[0.2em] font-bold text-slate-500">{title}</div>
        {extra}
      </div>
      {children}
    </div>
  );
}

function ColorField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      <div className="flex items-center gap-2 rounded-lg border border-slate-200 px-2 py-1.5 bg-white">
        <input
          type="color"
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-8 w-8 rounded cursor-pointer border-0 bg-transparent"
          data-testid={`${testid}-picker`}
        />
        <Input
          value={value}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="h-8 px-2 text-xs border-0 focus-visible:ring-0"
          data-testid={`${testid}-hex`}
        />
      </div>
    </div>
  );
}

function FontField({ label, value, onChange, testid }) {
  return (
    <div>
      <label className="text-[10px] uppercase tracking-wider text-slate-500 mb-1 block">{label}</label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="h-9 text-sm" data-testid={testid} style={{ fontFamily: fontCss(value) }}><SelectValue /></SelectTrigger>
        <SelectContent>
          {FONT_FAMILIES.map((f) => (
            <SelectItem key={f.id} value={f.id} style={{ fontFamily: f.css }}>{f.label}</SelectItem>
          ))}
        </SelectContent>
      </Select>
    </div>
  );
}
